// Audit tab — the project's audit log, newest first. Every console action
// (rebuild, cleanup, settings change, key/connector changes) and the
// file-level events the backend already recorded land here. Filters: action,
// target type, date range, free text; a row expands to its `extra` payload;
// CSV export streams the filtered set (capped server-side).

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Download, FileClock, Loader2, Search as SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadFile, ep } from '../lib/api.js';
import { usePagedList } from '../lib/usePagedList.js';
import { ListPagination } from './ListPagination.jsx';

const TARGET_TYPES = ['file', 'folder', 'project', 'api_key', 'connector', 'index_job', 'workflow'];

function fmt(value) {
  return value ? moment(value).format('DD MMM YYYY HH:mm:ss') : '—';
}

// The audit `action` vocabulary is open (`file.upload`, `indexing.rebuild`,
// …): the family before the dot gets a colour, the rest is shown verbatim.
const FAMILY_STYLE = {
  indexing: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800',
  settings: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  api_key: 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800',
  connector: 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800',
  file: 'bg-muted/50 text-foreground border-border',
};
function ActionChip({ action }) {
  const family = action.split('.')[0];
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 font-mono text-xs ${FAMILY_STYLE[family] || 'bg-muted/50 text-muted-foreground border-border'}`}>
      {action}
    </span>
  );
}

export const AuditTab = ({ projectId }) => {
  const { t } = useTranslation('console');
  const list = usePagedList(ep.audit(projectId), { size: 50 });
  const { filters, setFilter } = list;
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(false);
  const actions = list.extra?.actions || [];
  const items = list.items;

  const exportCsv = async () => {
    setExporting(true);
    const params = { format: 'csv' };
    if (list.search) params.search = list.search;
    for (const [k, v] of Object.entries(filters)) if (v && v !== 'all') params[k] = v;
    const res = await downloadFile(ep.audit(projectId), { params, filename: `audit-${projectId}.csv` });
    setExporting(false);
    if (!res.success) toast.error(res.message);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-56 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={list.searchInput} onChange={(e) => list.setSearchInput(e.target.value)} placeholder={t('audit.searchPlaceholder')} className="pl-9" />
        </div>
        <Select value={filters.action || 'all'} onValueChange={(v) => setFilter('action', v)}>
          <SelectTrigger className="w-52" aria-label={t('audit.table.action')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('audit.allActions')}</SelectItem>
            {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.target_type || 'all'} onValueChange={(v) => setFilter('target_type', v)}>
          <SelectTrigger className="w-44" aria-label={t('audit.table.target')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('audit.allTargets')}</SelectItem>
            {TARGET_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          {t('audit.from')}
          <Input type="date" className="h-9 w-40" value={filters.date_from || ''} onChange={(e) => setFilter('date_from', e.target.value)} aria-label={t('audit.from')} />
        </label>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          {t('audit.to')}
          <Input type="date" className="h-9 w-40" value={filters.date_to ? filters.date_to.slice(0, 10) : ''} onChange={(e) => setFilter('date_to', e.target.value ? `${e.target.value}T23:59:59` : '')} aria-label={t('audit.to')} />
        </label>
        {list.hasFilters && <Button variant="ghost" size="sm" onClick={list.clearFilters}>{t('list.clearFilters')}</Button>}
        <Button variant="outline" size="sm" className="ml-auto gap-2" onClick={exportCsv} disabled={exporting || items.length === 0}>
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {t('audit.exportCsv')}
        </Button>
      </div>

      {list.loading && items.length === 0 ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-blue-500 dark:text-blue-400 animate-spin" /></div>
      ) : items.length > 0 ? (
        <>
          <div className="w-full border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead className="w-44">{t('audit.table.time')}</TableHead>
                  <TableHead>{t('audit.table.action')}</TableHead>
                  <TableHead>{t('audit.table.actor')}</TableHead>
                  <TableHead>{t('audit.table.target')}</TableHead>
                  <TableHead className="w-28">{t('audit.table.request')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const open = expanded === row.id;
                  const hasExtra = row.extra && Object.keys(row.extra).length > 0;
                  return (
                    <Fragment key={row.id}>
                      <TableRow className={hasExtra ? 'cursor-pointer' : ''} onClick={() => hasExtra && setExpanded(open ? null : row.id)} data-row-id={row.id}>
                        <TableCell className="text-muted-foreground">
                          {hasExtra && (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(row.timestamp)}</TableCell>
                        <TableCell><ActionChip action={row.action} /></TableCell>
                        <TableCell className="text-sm">
                          {row.actor ? (
                            <span title={row.actor.email || ''}>{row.actor.display_name || row.actor.email || `#${row.actor.id}`}</span>
                          ) : (
                            <span className="text-muted-foreground">{t('audit.system')}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {row.target_type}{row.target_pk != null && <span className="font-mono text-xs text-muted-foreground"> #{row.target_pk}</span>}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-28" title={row.request_id || ''}>{row.request_id ? row.request_id.slice(0, 8) : '—'}</TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="bg-muted/50 hover:bg-muted/60">
                          <TableCell colSpan={6}>
                            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-card p-3 font-mono text-xs text-foreground" data-testid="audit-extra">
                              {JSON.stringify(row.extra, null, 2)}
                            </pre>
                            {row.ip_address && <p className="mt-1 text-xs text-muted-foreground">IP {row.ip_address}</p>}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <ListPagination list={list} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <FileClock className="h-12 w-12 mb-3 text-muted-foreground/60" />
          <p className="text-lg font-medium">{list.hasFilters ? t('list.noMatch') : t('audit.empty.title')}</p>
          <p className="text-sm text-muted-foreground">{list.hasFilters ? t('list.noMatchHint') : t('audit.empty.description')}</p>
          {list.hasFilters && <Button variant="ghost" size="sm" className="mt-2" onClick={list.clearFilters}>{t('list.clearFilters')}</Button>}
        </div>
      )}
    </div>
  );
};
